import type { Application } from "@judo/model-api";
import type { ModelRegistry, RegistrySnapshot } from "@judo/model-loader";
import { type ReactNode, createContext, useCallback, useContext, useSyncExternalStore } from "react";

/**
 * Application context type.
 */
export interface ApplicationContextType {
	/** Current active application (model required) */
	application: Application;
	/** All available applications (multi-actor) */
	applications: Application[];
	/** Switch to different actor */
	switchActor: (actorName: string) => void;
	/** Switch to different actor with navigation callback */
	switchActorWithNavigation: (actorName: string, onSwitch?: (app: Application) => void) => void;
	/** Current actor name */
	actorName: string;
}

const ApplicationContext = createContext<ApplicationContextType | null>(null);

/**
 * Hook to access application context.
 * Throws if used outside provider or without model.
 *
 * @returns ApplicationContextType
 * @throws Error if used outside ApplicationProvider
 */
export function useApplication(): ApplicationContextType {
	const context = useContext(ApplicationContext);
	if (!context) {
		throw new Error(
			"useApplication must be used within ApplicationProvider. " +
				"Ensure model is loaded before rendering application."
		);
	}
	return context;
}

/**
 * Hook to access application context (optional).
 * Returns null if not within provider instead of throwing.
 *
 * @returns ApplicationContextType | null
 */
export function useApplicationOptional(): ApplicationContextType | null {
	return useContext(ApplicationContext);
}

/**
 * Props for ApplicationProvider.
 */
export interface ApplicationProviderProps {
	children: ReactNode;
	registry: ModelRegistry;
}

/**
 * Provider requiring loaded model.
 * Uses useSyncExternalStore for proper subscription to registry changes.
 */
export function ApplicationProvider({ children, registry }: ApplicationProviderProps) {
	const subscribe = useCallback((onStoreChange: () => void) => registry.subscribe(onStoreChange), [registry]);

	const getSnapshot = useCallback((): RegistrySnapshot => registry.getSnapshot(), [registry]);

	const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

	if (!snapshot.activeApplication) {
		throw new Error("No active application. Model must be loaded first.");
	}

	const value: ApplicationContextType = {
		application: snapshot.activeApplication,
		applications: snapshot.applications,
		actorName: snapshot.activeActorName!,
		switchActor: (actorName: string) => registry.setActiveApplication(actorName),
		switchActorWithNavigation: (actorName: string, onSwitch?: (app: Application) => void) => {
			registry.setActiveApplication(actorName);
			if (onSwitch) {
				const newSnapshot = registry.getSnapshot();
				if (newSnapshot.activeApplication) {
					onSwitch(newSnapshot.activeApplication);
				}
			}
		},
	};

	return <ApplicationContext.Provider value={value}>{children}</ApplicationContext.Provider>;
}
